// User-Preferences-Store. Vereinigt die UI-Settings, die bisher in
// localStorage lebten, hinter einem reactive Server-SSOT-Cache:
//
//   - theme (Auto/Hell/Dunkel)
//   - lang (de/en)
//   - bg-settings (Hintergrund-Stil)
//   - favorites, action-favorites
//   - ui-prefs (Section-Pinning, Expand-States, ...)
//
// Lifecycle:
//   1. main.ts ruft initPrefsFromCache() vor App-Mount -> localStorage
//      hydratisiert den In-Memory-Store. Kein Server-Roundtrip, also
//      keinen Flash beim First-Paint (Theme/Sprache stehen sofort).
//   2. Nach Auth-OK ruft App.svelte loadPrefsFromServer(userID) auf.
//      Server-Werte ueberschreiben den lokalen Cache; Keys, die NUR
//      lokal existieren, werden einmalig hochgeladen (Migration).
//   3. setPref(key, value) updated optimistic lokal + cache + PATCH.
//   4. WS-Channel "prefs:<user_id>" empfaengt Patches von anderen
//      Tabs/Geraeten -- die werden ohne erneuten Roundtrip eingespielt.
//
// Werte werden generisch als JSON behandelt: getPref<T> liefert
// undefined wenn Key fehlt; Caller setzt Default selbst (analog zur
// frueheren localStorage-Lese-Logik).
//
// Seit 10.10.2026 folgt ein kleiner Teil dem GERAET statt dem Benutzer
// (Tabs, Leisten, Hell/Dunkel): getGeraetPref / setGeraetPref. Welche
// Schluessel das sind und nach welchen Regeln, steht in prefs-geraet.ts.

import { SvelteMap } from "svelte/reactivity";
import { getJSON, patchJSON } from "./api";
import {
  GERAET_SPEICHER,
  arbeitsplatzVon,
  hatArbeitsplatz,
  leseGeraet,
  liesFuerGeraet,
  schreibeFuerGeraet,
  uebernahme,
  verwaiste,
} from "./prefs-geraet";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

const CACHE_KEY = "homelab-dash-prefs-cache";

// In-Memory-Store. SvelteMap macht $derived-Konsumer reaktiv.
const _store = new SvelteMap<string, unknown>();

// Listener pro Key -- erlaubt Domain-Lib (z.B. theme.svelte.ts) eine
// gezielte Subscription ohne ueber den ganzen Map zu iterieren.
const _listeners = new Map<string, Set<(value: unknown) => void>>();

// Set der Keys, die seit dem letzten Server-Sync nur LOKAL existieren
// (also localStorage-Hydration, noch nicht im Server). loadPrefsFromServer
// uploadet sie einmalig nach dem Reconcile -- damit migrieren bestehende
// localStorage-Werte automatisch beim ersten authentifizierten Boot.
const _localOnly = new Set<string>();

// Active WS-Subscription. Wird beim Login geoeffnet, beim Logout (Reload)
// implizit weggeworfen.
let _wsOff: (() => void) | undefined;

// ─── Public API ─────────────────────────────────────────────────────

/** Liest einen Pref-Wert. undefined wenn nicht gesetzt. */
export function getPref<T = unknown>(key: string): T | undefined {
  return _store.get(key) as T | undefined;
}

/**
 * Setzt einen Pref-Wert. Optimistic local update + Cache + PATCH zum
 * Server. Wenn der Server-Call fehlschlaegt, bleibt der lokale Wert --
 * der naechste loadPrefsFromServer-Sync raeumt das auf. fire-and-forget
 * im Sinne von "wir blockieren keine UI darauf".
 *
 * value=null loescht den Eintrag (server + lokal).
 */
export function setPref(key: string, value: unknown): void {
  void setPrefs({ [key]: value });
}

/**
 * Mehrere Werte in EINEM Zug: ein lokaler Stand, ein PATCH, eine Meldung
 * an die anderen Fenster. Liefert, ob der Server sie angenommen hat --
 * wer danach neu laedt, muss das abwarten (sonst gewinnt beim Start der
 * alte Server-Stand gegen den frischen Cache).
 */
export async function setPrefs(patch: Record<string, unknown>): Promise<boolean> {
  const keys = Object.keys(patch);
  if (keys.length === 0) return true;
  for (const key of keys) applyLocalChange(key, patch[key]);
  persistCache();
  try {
    await patchJSON("/v2-api/me/prefs", patch);
    return true;
  } catch {
    // Soft-Fail: ohne Server kann der Wert nicht persistiert werden,
    // bleibt aber im Cache fuer den Reload. Bei nachster GET-Sync wird
    // entweder der lokale Wert hochgespielt (localOnly-Pfad) oder vom
    // Server-Wert ueberschrieben.
    for (const key of keys) _localOnly.add(key);
    return false;
  }
}

// ─── Je Geraet ──────────────────────────────────────────────────────
//
// Welches Geraet diese Sitzung ist, sagt der Server (auth/status). Der
// Browser merkt es sich NEBEN dem Cache, damit Tabs und Hell/Dunkel beim
// naechsten Start ohne Flackern stehen -- vor der ersten Antwort.
let _geraet = $state<number | null>(null);

/** Die Kennung dieses Geraets (Liste "Angemeldete Geraete"); null, wenn
 *  die Sitzung keines hat (Debug-Sitzung) oder es noch nicht feststeht. */
export function geraet(): number | null {
  return _geraet;
}

/** Vom Boot zu rufen, sobald der Server das Geraet genannt hat -- VOR
 *  loadPrefsFromServer, das danach die Vorlage uebernimmt. */
export function setzeGeraet(id: number | null): void {
  _geraet = id;
  try {
    if (id == null) localStorage.removeItem(GERAET_SPEICHER);
    else localStorage.setItem(GERAET_SPEICHER, String(id));
  } catch {
    // privater Modus -- dann eben ohne Gedaechtnis.
  }
}

const liesStore = (key: string): unknown => _store.get(key);

/** Liest einen Wert, der dem Geraet folgt: der eigene, sonst die Vorlage. */
export function getGeraetPref<T = unknown>(key: string): T | undefined {
  return liesFuerGeraet(liesStore, _geraet, key) as T | undefined;
}

/** Schreibt einen Wert fuer dieses Geraet (und fuehrt die Vorlage mit). */
export function setGeraetPref(key: string, value: unknown): void {
  void setPrefs(schreibeFuerGeraet(_geraet, key, value));
}

/** Liegt fuer dieses andere Geraet ein Arbeitsplatz, den man holen kann? */
export function geraetHatArbeitsplatz(id: number): boolean {
  return hatArbeitsplatz(liesStore, id);
}

/** "Arbeitsplatz von Geraet X uebernehmen": Tabs und Leisten von dort
 *  werden die eigenen. Liefert true, wenn der Server sie hat -- der
 *  Aufrufer laedt dann neu, damit die Shell sie herstellt. */
export async function uebernimmArbeitsplatz(von: number): Promise<boolean> {
  const patch = arbeitsplatzVon(liesStore, von, _geraet);
  if (Object.keys(patch).length === 0) return false;
  return setPrefs(patch);
}

/**
 * Subscribe auf Aenderungen eines bestimmten Keys. Caller bekommt den
 * aktuellen Wert sofort als ersten Call, danach bei jeder Aenderung.
 * Returnt eine Cleanup-Funktion.
 */
export function subscribePref(
  key: string,
  cb: (value: unknown) => void,
): () => void {
  let set = _listeners.get(key);
  if (!set) {
    set = new Set();
    _listeners.set(key, set);
  }
  set.add(cb);
  // Initial-Call mit aktuellem Wert -- so muss der Konsument nicht
  // selber getPref nach dem Mount aufrufen.
  cb(_store.get(key));
  return () => {
    const s = _listeners.get(key);
    if (s) s.delete(cb);
  };
}

// _geladen: steht der Server-Stand? Vorher ist "der Wert ist nicht
// gesetzt" nicht dasselbe wie "es gibt keinen Wert" -- der Unterschied
// hat am 21.08. die Tab-Anordnung gekostet (siehe merkeLayout in
// shell/tabs.svelte.ts).
let _geladen = $state(false);

/** true, sobald der Server-Stand eingespielt ist ODER feststeht, dass
 *  keiner mehr kommt. Wer aus Prefs einen ZUSTAND wiederherstellt, muss
 *  das abwarten -- sonst schreibt er seinen leeren Anfangszustand
 *  ueber den gemerkten. */
export function prefsGeladen(): boolean {
  return _geladen;
}

/** Vom Boot zu rufen, wenn KEIN Server-Sync kommt (nicht angemeldet,
 *  keine UserID). Ohne das warten die Wiederhersteller ewig. */
export function markierePrefsGeladen(): void {
  _geladen = true;
}

/**
 * Hydratisiert den Store aus dem localStorage-Cache. Muss VOR App-Mount
 * laufen, damit Theme/Sprache ohne Flash stehen.
 */
export function initPrefsFromCache(): void {
  try {
    // Zuerst das Geraet: wer gleich Hell/Dunkel oder die Tabs liest, soll
    // den Stand DIESES Geraets finden, nicht die Vorlage.
    _geraet = leseGeraet(localStorage.getItem(GERAET_SPEICHER));
  } catch {
    // privater Modus
  }
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    for (const [k, v] of Object.entries(parsed)) {
      // Ueber applyLocalChange statt _store.set: Module-Scope-
      // Subscriber (z.B. bg-settings) registrieren sich waehrend des
      // Bundle-Imports, also VOR diesem Aufruf aus main.ts -- ihr
      // Initial-Call sah nur undefined (=Defaults). Ohne Notify
      // bootete der Hintergrund mit dem farbnebel-Default und sprang
      // erst nach dem Server-Sync auf das konfigurierte Theme um
      // (sichtbarer Blob-"Punkt" beim Reload).
      applyLocalChange(k, v);
      _localOnly.add(k);
    }
  } catch {
    // Corrupt cache -- ignorieren, sauber neu beginnen.
  }
}

/**
 * Holt den aktuellen Pref-State vom Server und reconciled. Server
 * gewinnt bei Konflikt; Keys, die nur lokal existieren (frischer
 * localStorage-Boot vor diesem Refactor), werden einmalig hochgeladen.
 */
export async function loadPrefsFromServer(userID: number): Promise<void> {
  try {
    const server = await getJSON<Record<string, unknown>>("/v2-api/me/prefs");
    // Der Arbeitsplatz ANDERER Geraete gehoert dem Server: fuehrt er ihn
    // nicht mehr (Geraet abgemeldet), faellt er auch hier weg -- sonst
    // lueden wir ihn gleich wieder hoch.
    const weg = new Set(verwaiste([..._store.keys()], server, _geraet));
    for (const key of weg) applyLocalChange(key, null);
    // Reconcile: Server-Werte ueberschreiben lokale; localOnly-Keys, die
    // NICHT im Server-Set sind, gehen als Migration nach oben.
    const toMigrate: Record<string, unknown> = {};
    for (const key of _localOnly) {
      if (!(key in server) && !weg.has(key)) {
        toMigrate[key] = _store.get(key);
      }
    }
    // Server-Werte einspielen -- ueberschreibt lokal.
    for (const [k, v] of Object.entries(server)) {
      applyLocalChange(k, v);
    }
    // Was diesem Geraet noch fehlt, bekommt es aus der Vorlage. NUR hier,
    // nach gelungenem Lesen: aus einem kalten Cache heraus hiesse "kein
    // eigener Wert" nicht "es gibt keinen" (prefs-geraet.ts, uebernahme).
    const uebernommen = uebernahme(liesStore, _geraet);
    for (const [k, v] of Object.entries(uebernommen)) {
      applyLocalChange(k, v);
      toMigrate[k] = v;
    }
    persistCache();
    _localOnly.clear();

    if (Object.keys(toMigrate).length > 0) {
      // One-time-Migration: bestehende localStorage-Werte rauf zum
      // Server. Fehler hier sind nicht fatal -- nachster setPref wird
      // sie regulaer hochladen.
      await patchJSON("/v2-api/me/prefs", toMigrate).catch(() => {});
    }
  } catch {
    // GET failed (Netz weg, 401-Race) -- der lokale Cache bleibt
    // gueltig. Naechster Bootversuch zieht ihn wieder.
  } finally {
    // AUCH im Fehlerfall: die Wiederhersteller duerfen nicht ewig
    // warten. Der Cache ist dann die beste verfuegbare Wahrheit.
    _geladen = true;
  }

  // WS-Subscribe fuer Live-Sync. Stale-Subscription killen, dann neu.
  if (_wsOff) {
    _wsOff();
    _wsOff = undefined;
  }
  _wsOff = wsSubscribe(`prefs:${userID}`, (data: unknown) => {
    if (!data || typeof data !== "object") return;
    const patch = (data as { patch?: unknown }).patch;
    if (!patch || typeof patch !== "object") return;
    for (const [k, v] of Object.entries(patch as Record<string, unknown>)) {
      applyLocalChange(k, v);
    }
    persistCache();
  });
}

// ─── Intern ─────────────────────────────────────────────────────────

function applyLocalChange(key: string, value: unknown): void {
  if (value === null) {
    _store.delete(key);
  } else {
    _store.set(key, value);
  }
  const subs = _listeners.get(key);
  if (subs) {
    const v = _store.get(key);
    for (const cb of subs) {
      try {
        cb(v);
      } catch {
        // Listener-Fehler darf andere Listener nicht killen.
      }
    }
  }
}

function persistCache(): void {
  try {
    const obj: Record<string, unknown> = {};
    for (const [k, v] of _store.entries()) {
      obj[k] = v;
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(obj));
  } catch {
    // Quota / privater Modus -- still ignorieren.
  }
}
